import type { Root } from "react-dom/client";
import { mountWidget } from "./widget";
import type { MountOptions, WidgetHandle } from "./types";

declare var __CB_AICSR_CSS__: string;

const VERSION = "0.3.0";

function mount(options: MountOptions): WidgetHandle {
  if (!options?.target) {
    throw new Error("CBAicsr.mount: `target` HTMLElement is required");
  }
  if (!options.runtimeUrl && !options.publicApiKey) {
    throw new Error("CBAicsr.mount: provide `runtimeUrl` (self-hosted) or `publicApiKey` (CopilotKit Cloud)");
  }

  const host = options.target;
  const shadow = host.attachShadow({ mode: "open" });

  // Inject bundled CSS (CopilotKit styles + theme overrides) into shadow root.
  // adoptedStyleSheets is supported in all modern browsers (Chrome 73+, FF 101+,
  // Safari 16.4+). Falls back to a <style> element for older browsers.
  if (typeof __CB_AICSR_CSS__ === "string" && __CB_AICSR_CSS__.length > 0) {
    if (shadow.adoptedStyleSheets !== undefined) {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(__CB_AICSR_CSS__);
      shadow.adoptedStyleSheets = [sheet];
    } else {
      const style = document.createElement("style");
      style.textContent = __CB_AICSR_CSS__;
      shadow.appendChild(style);
    }
  }

  const container = document.createElement("div");
  container.className = "cb-aicsr-container";
  shadow.appendChild(container);

  // Stop mouse/pointer events from crossing the shadow boundary.
  // CopilotKit's clickOutsideToClose handler listens on `document` — when
  // events exit the shadow DOM, event.target is retargeted to the shadow host,
  // which CopilotKit interprets as "outside the popup" and closes it.
  for (const evt of ["mousedown", "pointerdown"] as const) {
    host.addEventListener(evt, (e) => e.stopPropagation());
  }

  const root: Root = mountWidget(container, {
    runtimeUrl: options.runtimeUrl,
    publicApiKey: options.publicApiKey,
    agent: options.agentId ?? "agent",
    pdfCdnBase: options.pdfCdnBase,
    pdfWorkerUrl: options.pdfWorkerUrl,
    customer: options.customer,
    cart: options.cart,
    shadowRoot: shadow,
  });

  return {
    unmount() {
      root.unmount();
      host.shadowRoot?.replaceChildren();
    },
  };
}

if (typeof window !== "undefined") {
  window.CBAicsr = { mount, version: VERSION };
}

export { mount, VERSION };
